import { getApiErrorDetail, useApiClient } from '@klareospace/pharos-lib-front-utils';
import { CARRIER_CASCADE_MODE } from '@klareospace/pharos-lib-types';
import type { Carrier } from '@klareospace/pharos-lib-types';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { driverKeys } from '../../drivers/api';
import { vehicleKeys } from '../../vehicles/api';
import { carrierKeys, createCarrierApi } from '../api';
import type { UnreferenceCarrierRequest } from '../api';

export function useUnreferenceCarrier(): UseMutationResult<Carrier, Error, UnreferenceCarrierRequest> {
  const client = useApiClient('admin-auth');
  const api = createCarrierApi(client);
  const queryClient = useQueryClient();
  const { t } = useTranslation('settings');

  return useMutation({
    mutationFn: (request: UnreferenceCarrierRequest) => api.unreferenceCarrier(request),
    onError: (error: Error) => {
      const detail = getApiErrorDetail(error);
      toast.error(detail.message || t('spaces.settings.carriers.toast.unreferenceError'));
    },
    onSuccess: (_result, variables) => {
      toast.success(t('spaces.settings.carriers.toast.unreferenced'));
      void queryClient.invalidateQueries({ queryKey: carrierKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: carrierKeys.detail(variables.carrierId) });
      if (variables.cascadeMode === CARRIER_CASCADE_MODE.CASCADE) {
        void queryClient.invalidateQueries({ queryKey: driverKeys.all });
        void queryClient.invalidateQueries({ queryKey: vehicleKeys.all });
      }
    },
  });
}
